import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Loader2,
  AlertCircle,
  IndianRupee,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RotateCcw,
  X,
} from "lucide-react";
import { ToastContainer } from "react-toastify";
import Loader from "../../components/Loader/Loader";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import {
  ServiceRevenueService,
  fetchVendorServiceUsage,
} from "../../services/customerService";
import ReportHeading from "../Components/ReportHeadings/ReportHeadings";

const RevenueReport = () => {
  const getServerErrorMessage = (error, fallback = "Failed to fetch data") => {
    const errorData = error?.response?.data || error?.data || {};

    if (errorData?.errors && typeof errorData.errors === "object") {
      const firstFieldErrors = Object.values(errorData.errors)[0];
      if (Array.isArray(firstFieldErrors) && firstFieldErrors[0]) {
        return firstFieldErrors[0];
      }
      if (typeof firstFieldErrors === "string") {
        return firstFieldErrors;
      }
    }

    return (
      errorData?.status?.message ||
      errorData?.message ||
      error?.message ||
      fallback
    );
  };

  const getResponseErrorMessage = (
    response,
    fallback = "Failed to load report",
  ) => {
    if (response?.errors && typeof response.errors === "object") {
      const firstFieldErrors = Object.values(response.errors)[0];
      if (Array.isArray(firstFieldErrors) && firstFieldErrors[0]) {
        return firstFieldErrors[0];
      }
      if (typeof firstFieldErrors === "string") {
        return firstFieldErrors;
      }
    }

    return response?.status?.message || response?.message || fallback;
  };

  // Get today's date in YYYY-MM-DD format [web:39][web:44]
  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  // Define initial filter state with today's date [web:39][web:44]
  const initialFilters = {
    from_date: getTodayDate(),
    to_date: getTodayDate(),
    vendor_id: "",
    service_id: "",
    per_page: 50,
  };

  const [filters, setFilters] = useState(initialFilters);
  const [reportData, setReportData] = useState([]);
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [serviceList, setServiceList] = useState([]);
  const [backupServiceList, setBackupServiceList] = useState([]);
  const [debouncedServiceSearch, setDebouncedServiceSearch] = useState(null);
  const [showServiceList, setShowServiceList] = useState(false);
  const [serviceName, setServiceName] = useState("");

  const isInitialMount = useRef(true);

  const perPageOptions = [10, 25, 50, 100, 200];

  // Fetch report function
  const fetchReport = async (page = 1) => {
    try {
      setLoading(true);

      const params = Object.fromEntries(
        Object.entries(filters).filter(
          ([key, value]) => value && key !== "per_page",
        ),
      );

      params.per_page = filters.per_page;

      const response = await ServiceRevenueService(params, page);

      if (Array.isArray(response?.data)) {
        setReportData(response.data);

        if (response.pagination) {
          setTotalPages(response.pagination.last_page || 1);
          setTotalRecords(response.pagination.total || 0);
          setCurrentPage(response.pagination.current_page || 1);
        }

        // showSuccessToast(`Loaded ${response.data.length} records`);
      } else {
        showErrorToast(
          getResponseErrorMessage(response, "Failed to load report"),
        );
        setReportData([]);
      }
    } catch (error) {
      showErrorToast(getServerErrorMessage(error, "Failed to fetch data"));
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Load services and initial data on mount
  useEffect(() => {
    const loadServices = async () => {
      try {
        const response = await fetchVendorServiceUsage();
        if (response?.data?.services?.data)
           setBackupServiceList(response?.data?.services?.data);
           setServiceList(response?.data?.services?.data);
      } catch (error) {
        showErrorToast("Failed to load services");
      }
    };

    loadServices();
    fetchReport(1); // Fetch today's data by default [web:44]
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Auto-fetch for non-date filters (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setCurrentPage(1);
    fetchReport(1);
  }, [filters.vendor_id, filters.service_id, filters.per_page]);

  // Fetch on page change
  useEffect(() => {
    if (currentPage > 1) {
      fetchReport(currentPage);
    }
  }, [currentPage]);

  // Reset all filters and reload today's data [web:44]
  const handleReset = () => {
    setFilters({
      // from_date: getTodayDate(),
      // to_date: getTodayDate(),
      from_date: "",
      to_date: "",
      vendor_id: "",
      service_id: "",
      per_page: 50,
    });
    setSearchTerm("");
    setCurrentPage(1);
    isInitialMount.current = false;

    setTimeout(() => {
      fetchReport(1);
    }, 0);
  };

  // Client-side filtering by search term
  const safeReportData = Array.isArray(reportData) ? reportData : [];
  const filteredData = safeReportData.filter(
    (item) =>
      !debouncedSearch ||
      item.service_name
        ?.toLowerCase()
        .includes(debouncedSearch.toLowerCase()) ||
      item.end_point?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      item.service_code?.toLowerCase().includes(debouncedSearch.toLowerCase()),
  );

  // Calculate summary from filtered data with CORRECT Final Profit
  const summaryData = filteredData.reduce(
    (acc, item) => {
      const collection = parseFloat(item.total_amount || 0);
      const charges =
        parseFloat(item.vendor_total_charge || 0) ||
        parseFloat(item.vendor_request || 0) *
          parseFloat(item.vendor_per_request_charge || 0);
      const earning =
        parseFloat(item.cache_response_earning || 0) ||
        parseFloat(item.cache_responses || 0) *
          parseFloat(item.cache_per_request_price || 0);

      // Calculate Final Profit: (Collection - Cache Earning - Vendor Charges) + Cache Earning
      // Simplified: Collection - Vendor Charges
      const finalProfit = collection - charges;

      return {
        totalAmount: acc.totalAmount + collection,
        totalGst: acc.totalGst + parseFloat(item.total_gst || 0),
        vendorCharges: acc.vendorCharges + charges,
        cacheEarning: acc.cacheEarning + earning,
        finalProfit: acc.finalProfit + finalProfit, //  Sum of all final profits
      };
    },
    {
      totalAmount: 0,
      totalGst: 0,
      vendorCharges: 0,
      cacheEarning: 0,
      finalProfit: 0,
    },
  );

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchReport(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxShow = 5;

    if (totalPages <= maxShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages,
        );
      }
    }
    return pages;
  };

  const getStartRecord = () =>
    totalRecords === 0 ? 0 : (currentPage - 1) * filters.per_page + 1;
  const getEndRecord = () =>
    Math.min(currentPage * filters.per_page, totalRecords);

  // client side service search

  useEffect(() => {
    if (serviceName === "") setServiceList(backupServiceList);

    const target = serviceName?.toLowerCase()?.trim();

    const match = backupServiceList?.filter((item, idx) => {
      return item?.service_name?.toLowerCase().includes(target);
    });
    setServiceList(match);
  }, [serviceName]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="p-3 md:p-5 w-full mx-auto"
    >
      {loading && <Loader />}
      <ToastContainer />

      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-3 md:p-2 md:mt-6 mt-4">
        <ReportHeading name="API Revenue Report" />

        {/* Filters with Reset Button */}
        <div className="bg-gray-50 p-4 rounded-lg shadow-sm mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                From Date
              </label>
              <input
                type="date"
                name="from_date"
                value={filters.from_date}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">To Date</label>
              <input
                type="date"
                name="to_date"
                value={filters.to_date}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* <div>
              <label className="block text-sm font-medium mb-1">Vendor ID (Optional)</label>
              <input
                type="number"
                name="vendor_id"
                value={filters.vendor_id}
                onChange={handleFilterChange}
                placeholder="Enter Vendor ID"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div> */}

            {/* <div>
              <label className="block text-sm font-medium mb-1">Select Service</label>
              <select
                name="service_id"
                value={filters.service_id}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Services</option>
                {services?.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.service_name} ({service.service_code})
                  </option>
                ))}
              </select>
            </div> */}

            <div className="">
              <label
                htmlFor="service_name"
               className="block text-sm font-medium mb-1"
              >
                Service Name
              </label>
              <input
                onFocus={() => setShowServiceList(true)}
                // onBlur={() => setShowServiceList(false)}
                type="search"
                autoComplete="off"
                name="service_name"
                id="service_name"
                placeholder="Service Name"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={serviceName}
                onChange={(e) => {
                  setServiceName(e.target.value);
                }}
              />

              {/* Dropdown Container */}
              {serviceList?.length > 0 && showServiceList && (
                <div className="relative">
                  <div className="absolute top-full left-0 mt-2 w-72 z-30 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
                      <h2 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                        Select Service
                      </h2>
                      <button
                        onClick={() => setShowServiceList(false)}
                        className="p-1 rounded-lg text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
                        aria-label="Close menu"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Service List Options */}
                    <div className="py-1 max-h-60 overflow-y-auto divide-y divide-zinc-50 dark:divide-zinc-800/50">
                      {serviceList.map((item, idx) => (
                        <button
                          key={item?.id || idx} // Highly recommended to use a unique ID instead of index if available
                          onClick={() => {
                            setServiceName(item?.service_name);
                            setFilters((prev) => ({
                              ...prev,
                              service_id: item.id,
                            }));
                            setShowServiceList(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors block layout-fixed truncate"
                        >
                          {item?.service_name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={handleApplyFilters}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded disabled:bg-blue-400 transition-colors"
              >
                Apply Filters
              </button>
              <button
                onClick={handleReset}
                disabled={loading}
                className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-6 rounded disabled:bg-gray-400 transition-colors flex items-center gap-2"
              >
                <RotateCcw size={18} />
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {filteredData.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            {[
              {
                label: "Total Hit Revenue",
                value: summaryData.totalAmount,
                color: "blue",
              },
              {
                label: "Total GST",
                value: summaryData.totalGst,
                color: "orange",
              },
              {
                label: "Vendor Charges",
                value: summaryData.vendorCharges,
                color: "red",
              },
              {
                label: "Cache Earning",
                value: summaryData.cacheEarning,
                color: "green",
              },
              {
                label: "Final Profit",
                value: summaryData.finalProfit,
                color: "green-700",
              },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 text-center"
              >
                <h6 className="text-sm text-gray-600 mb-2">{label}</h6>
                <div
                  className={`text-xl font-bold text-${color}-600 flex items-center justify-center`}
                >
                  <IndianRupee size={20} className="mr-1" />
                  {formatCurrency(value).replace("₹", "")}
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Revenue Table */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4 border-b">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
              <div className="flex items-center gap-3">
                <h6 className="text-lg font-semibold">Revenue Breakdown</h6>
                {totalRecords > 0 && (
                  <span className="text-sm text-gray-600">
                    ({getStartRecord()}-{getEndRecord()} of {totalRecords})
                  </span>
                )}
              </div>

              <div className="flex gap-3 w-full md:w-auto">
                <select
                  value={filters.per_page}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      per_page: parseInt(e.target.value),
                    }))
                  }
                  disabled={loading}
                  className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  {perPageOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt} per page
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search Service / Endpoint / Code"
                  className="border border-gray-300 rounded px-3 py-2 w-full md:w-80 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-blue-800 text-white">
                <tr>
                  {[
                    "SRN",
                    "Vendor Name",
                    "Service Name",
                    "End Point",
                    "Total Requests",
                    "Total Collection",
                    "Vendor Charges",
                    "Cache Earning",
                    "Difference Earning",
                    "Final Profit",
                  ].map((h) => (
                    <th
                      key={h}
                      className={`px-2 py-1 text-${
                        h.includes("Collection") ||
                        h.includes("Charges") ||
                        h.includes("Earning") ||
                        h.includes("Profit") ||
                        h.includes("Requests")
                          ? "right"
                          : "left"
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {filteredData.length === 0 ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center py-12 text-gray-500"
                    >
                      <AlertCircle className="mx-auto mb-2" size={40} />
                      <p className="text-lg font-medium">No records found</p>
                      <p className="text-sm mt-1">
                        Try adjusting your filters or search term
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, index) => {
                    // Total Collection = Total Amount (already calculated: total_requests × price)
                    const collection = parseFloat(item.total_amount || 0);

                    // Vendor Charges = Vendor Requests × Vendor Charge per Request
                    const charges =
                      parseFloat(item.vendor_total_charge || 0) ||
                      parseFloat(item.vendor_request || 0) *
                        parseFloat(item.vendor_per_request_charge || 0);

                    // Cache Earning = Cache Responses × Cache Price (or use pre-calculated)
                    const earning =
                      parseFloat(item.cache_response_earning || 0) ||
                      parseFloat(item.cache_responses || 0) *
                        parseFloat(item.cache_per_request_price || 0);

                    // Difference Earning = Total Collection - Cache Earning - Vendor Charges
                    const differenceEarning = collection - earning - charges;

                    // Final Profit = Difference Earning + Cache Earning
                    const finalProfit = differenceEarning + earning;

                    return (
                      <tr
                        key={item.service_id || index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        {/* SRN */}
                        <td className="px-2 border py-1 text-center">
                          {(currentPage - 1) * filters.per_page + index + 1}
                        </td>

                        {/* Vendor Name */}
                        <td className="px-2 border py-1 font-medium">
                          {item.vendor_name || "N/A"}
                        </td>

                        {/* Service Name */}
                        <td className="px-2 border py-1 font-medium text-[12px]">
                          {item.service_name || "N/A"}
                        </td>

                        {/* End Point */}
                        <td className="px-2 border py-1 text-sm font-mono text-gray-600">
                          {item.end_point || "N/A"}
                        </td>

                        {/* Total Requests */}
                        <td className="px-2 border py-1 text-right text-blue-700 font-semibold">
                          {parseFloat(item.total_requests || 0).toLocaleString(
                            "en-IN",
                          )}
                        </td>

                        {/* Total Collection */}
                        <td className="px-2 border py-1 text-right text-blue-700 font-bold">
                          {formatCurrency(collection)}
                        </td>

                        {/* Vendor Charges */}
                        <td className="px-2 border py-1 text-right text-red-600 font-bold">
                          {formatCurrency(charges)}
                        </td>

                        {/* Cache Earning */}
                        <td className="px-2 border py-1 text-right text-green-600 font-bold">
                          {formatCurrency(earning)}
                        </td>

                        {/* Difference Earning */}
                        <td
                          className={`px-2 border py-1 text-right font-bold ${
                            differenceEarning >= 0
                              ? "text-green-700"
                              : "text-red-700"
                          }`}
                        >
                          {formatCurrency(differenceEarning)}
                        </td>

                        {/* Final Profit */}
                        <td
                          className={`px-2 border py-1 text-right font-bold ${
                            finalProfit >= 0 ? "text-green-800" : "text-red-800"
                          }`}
                        >
                          {formatCurrency(finalProfit)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 p-4 border-t bg-gray-50">
              <div className="text-sm text-gray-600">
                Showing{" "}
                <span className="font-semibold">{getStartRecord()}</span> to{" "}
                <span className="font-semibold">{getEndRecord()}</span> of{" "}
                <span className="font-semibold">{totalRecords}</span> entries
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                  className="p-2 border border-gray-300 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title="First Page"
                >
                  <ChevronsLeft size={18} />
                </button>

                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <ChevronLeft size={18} />
                  <span className="hidden sm:inline">Previous</span>
                </button>

                <div className="flex gap-1">
                  {getPageNumbers().map((page, index) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-2 text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        disabled={loading}
                        className={`px-3 py-2 border rounded transition-colors ${
                          currentPage === page
                            ? "bg-blue-600 text-white border-blue-600 font-semibold"
                            : "border-gray-300 hover:bg-white"
                        } disabled:cursor-not-allowed`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight size={18} />
                </button>

                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || loading}
                  className="p-2 border border-gray-300 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Last Page"
                >
                  <ChevronsRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default RevenueReport;
